/**
 * TD Cart Upsell — variant swatches for the cart drawer upsell card.
 *
 * The swatches are radios named "id", so they ARE the control product-form.js
 * submits. This element only keeps price, compare-at and colour label in step
 * with the checked one, and remembers the choice across cart re-renders.
 *
 * cart-drawer.js re-renders the drawer with innerHTML, which destroys and
 * rebuilds this element on every cart operation — connectedCallback firing on
 * each new instance is exactly the lifecycle hook that needs, so listeners bind
 * fresh and restore() runs without anything watching for it.
 *
 * Registered from an asset rather than an inline <script>, because scripts
 * inserted via innerHTML never execute: a cart that was empty at page load
 * would gain the markup with no definition behind it. Loaded in
 * layout/theme.liquid.
 */

if (!customElements.get('td-cart-upsell')) {
  class TdCartUpsell extends HTMLElement {
    static SELECTORS = {
      card: '[data-cart-upsell]',
      swatch: '[data-cart-upsell-variant]',
      colourLabel: '[data-cart-upsell-value]',
      price: '[data-cart-upsell-price]',
      compare: '[data-cart-upsell-compare]',
    };

    static STORAGE_PREFIX = 'td:cart-upsell:variant:';

    static DISCOUNTED_CLASS = 'cart-item__upsell--discounted';

    connectedCallback() {
      /* Abort any previous controller first. connectedCallback can fire again
         without an intervening disconnect (adoptNode, or a re-insert in the
         same tick), which would otherwise bind a second `change` listener and
         orphan the controller that could have released the first. abort() is
         idempotent, so this is safe on the normal first-connect path too. */
      this.listenerController?.abort();
      this.listenerController = new AbortController();

      this.addEventListener('change', this.onVariantChange.bind(this), {
        signal: this.listenerController.signal,
      });

      this.restore();
    }

    disconnectedCallback() {
      /* Optional-chained: an element upgraded and removed within the same tick
         reaches here with no controller assigned. The `change` listener is the
         only thing this component registers — no timers, observers or
         document-level handlers — so aborting the signal is a complete
         teardown. */
      this.listenerController?.abort();
    }

    get card() {
      return this.querySelector(TdCartUpsell.SELECTORS.card);
    }

    get storageKey() {
      const card = this.card;
      const productId = card && card.dataset.cartUpsellProduct;
      return productId ? TdCartUpsell.STORAGE_PREFIX + productId : null;
    }

    /* sessionStorage throws in private mode and when the quota is full. The
       selection simply won't survive a re-render in that case. */
    readSelection() {
      const key = this.storageKey;
      if (!key) return null;
      try {
        return sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    }

    writeSelection(variantId) {
      const key = this.storageKey;
      if (!key || !variantId) return;
      try {
        sessionStorage.setItem(key, variantId);
      } catch (e) {
        /* Ignored — see readSelection. */
      }
    }

    onVariantChange(event) {
      const input = event.target.closest(TdCartUpsell.SELECTORS.swatch);
      if (!input) return;

      this.writeSelection(input.value);
      this.apply(input);
    }

    /**
     * data-price and data-compare are money-formatted by Liquid, so they go
     * straight to textContent — same as updatePrice() in td-cross-sell.js.
     */
    apply(input) {
      const card = this.card;
      if (!card) return;

      const { colourLabel, price, compare } = TdCartUpsell.SELECTORS;

      const label = card.querySelector(colourLabel);
      if (label) label.textContent = input.dataset.variantTitle || '';

      const priceEl = card.querySelector(price);
      if (priceEl && input.dataset.price) priceEl.textContent = input.dataset.price;

      const compareEl = card.querySelector(compare);
      if (compareEl) {
        if (input.dataset.compare) {
          compareEl.textContent = input.dataset.compare;
          compareEl.hidden = false;
        } else {
          compareEl.textContent = '';
          compareEl.hidden = true;
        }
      }

      /* Keep the green price treatment in step with whether THIS variant really
         has a compare-at price. */
      card.classList.toggle(TdCartUpsell.DISCOUNTED_CLASS, Boolean(input.dataset.compare));
    }

    /**
     * The card re-renders with its default variant checked, so without this a
     * quantity change silently resets the shopper's colour choice.
     */
    restore() {
      const stored = this.readSelection();
      if (!stored) return;

      /* Match by property rather than building a selector: a stored value
         containing a quote or bracket would make querySelector throw. */
      const input = Array.from(
        this.querySelectorAll(TdCartUpsell.SELECTORS.swatch)
      ).find((el) => el.value === stored);

      if (!input || input.disabled || input.checked) return;

      input.checked = true;
      this.apply(input);
    }
  }

  customElements.define('td-cart-upsell', TdCartUpsell);
}
